import { CollapseSection } from '@/components/ats';
import * as Yup from 'yup';
import { ArrayField } from '@/components/ats/hris/ArrayField';
import { Box, Flex } from '@chakra-ui/react';
import { KTag } from '@/ui-library/components';
import { capitalizeFirstLetter } from '@/utils/commonHelpers';
import { formatLocalDate } from '@/utils/dateUtils';

// Helper function to capitalize first letter and make rest lowercase
const capitalizeWords = (str) => {
  if (!str) return str;
  const strArr = str.split(' ');

  const processedWords = strArr.map((word, index) => {
    // Check if the word is fully capitalized (all letters are uppercase)
    const isFullyCapitalized = word === word.toUpperCase();

    if (isFullyCapitalized) {
      // If fully capitalized, leave as is for all words
      return word;
    } else {
      if (index === 0) {
        // First word: capitalize first letter if not all capital
        return capitalizeFirstLetter(word.toLowerCase());
      } else {
        // Other words: make all characters lowercase if not all capital
        return word.toLowerCase();
      }
    }
  });

  return processedWords.join(' ');
};

const NestedHeader = ({ title, children, ...props }) => (
  <Box {...props}>
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap="20px"
      py="8px"
    >
      <KTag value={title} whiteSpace="nowrap" />
      <hr style={{ width: '100%' }} />
    </Flex>
    <Box>{children}</Box>
  </Box>
);

// Helper function to create wrapper props with customizable styling
const createWrapperProps = (title, isNested = false) => ({
  title: title.toUpperCase(),
  isHeaderClickable: true,
  defaultIsOpen: true,
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: isNested ? 'ats-secondary' : 'ats-tertiary',
      p: isNested ? '10px' : '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: isNested ? '20px' : '24px',
      height: isNested ? '20px' : '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: isNested ? '4px' : '6px',
    ...(isNested && { marginTop: '8px' }),
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: isNested ? '0.8em' : '1em 1em',
    backgroundColor: isNested ? '#f8f9fa' : 'white',
  },
});

// Helper function to create section configuration
const createSectionConfig = (id, title, fields = [], isNested = false) => ({
  id,
  type: 'wrapper',
  fields,
  Wrapper: isNested ? NestedHeader : CollapseSection,
  wrapperProps: isNested
    ? { title: capitalizeWords(title) }
    : createWrapperProps(title, isNested),
});

// Helper function to process object properties and convert to fields
const processObjectProperties = (properties, keyPrefix) => {
  const fields = [];
  const nestedSections = [];

  Object.entries(properties).forEach(([childKey, childSchema]) => {
    const fullKey = `${keyPrefix}-${childKey}`;

    if (childSchema.type === 'object') {
      // Create nested section
      const nestedSection = createSectionConfig(
        fullKey,
        childSchema.label || childKey,
        [],
        true
      );

      // Add fields from nested object properties
      if (childSchema.properties) {
        const nestedFields = processObjectProperties(
          childSchema.properties,
          fullKey
        );
        nestedSection.fields.push(...nestedFields.fields);
        nestedSections.push(...nestedFields.nestedSections);
      }

      nestedSections.push(nestedSection);
    } else {
      // Regular field
      fields.push(convertFieldToFormConfig(fullKey, childSchema));
    }
  });

  return { fields, nestedSections };
};

// Helper function to create select field configuration
const createSelectConfig = (schema, isMulti = false) => {
  const mappedOptions =
    schema.options?.entries?.slice(0, 500)?.map((entry) => ({
      label: entry?.label || entry?.name || entry?.display_full_name,
      id: entry?.id,
    })) || [];

  const customLoadOptions = (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      // Return first 500 options for initial load
      return Promise.resolve(
        schema.options?.entries?.slice(0, 500)?.map((entry) => ({
          label: entry?.label || entry?.name || entry?.display_full_name,
          id: entry?.id,
        })) || []
      );
    }

    // Filter all options based on search input
    const searchLower = inputValue.toLowerCase();
    const filteredOptions =
      schema.options?.entries
        ?.filter((entry) => {
          const label =
            entry?.label || entry?.name || entry?.display_full_name || '';
          return label.toLowerCase().includes(searchLower);
        })
        ?.slice(0, 100) // Limit search results for performance
        ?.map((entry) => ({
          label: entry?.label || entry?.name || entry?.display_full_name,
          id: entry?.id,
        })) || [];

    return Promise.resolve(filteredOptions);
  };

  return {
    type: 'asyncSelect',
    options: mappedOptions,
    // defaultOptions: mappedOptions, // todo: removing for prefill issue
    valueKey: 'id',
    labelKey: 'label',
    isMulti,
    loadDefaultOptions: true,
    placeholder: `Search ${schema.label || 'options'}...`,
    customLoadOptions,
    ...(isMulti && { validation: getYupForField(schema) }),
    isClearable: true,
  };
};

export const convertFieldToFormConfig = (key, schema) => {
  const baseConfig = {
    name: key,
    label: capitalizeWords(schema.label || key),
    isRequired: schema.required || false,
    unifiedKey: schema.unified_key || '',
    placeholder: `Enter ${capitalizeWords(schema.label) || key}`,
    optionalValidation: true,
    validation: getYupForField(schema),
  };

  const fieldTypeConfigs = {
    text: { type: 'input' },
    number: { type: 'input', inputType: 'number' },
    date: {
      type: 'date',
      dateFormat: (date) => {
        if (!date) return '';

        return formatLocalDate(date);
      },
    },
    single_select: createSelectConfig(schema, false),
    checkbox: { type: 'checkbox' },
    multi_select: createSelectConfig(schema, true),
    file: {
      type: 'upload',
      appendFiles: false,
      maxSize: schema.file_restrictions?.max_file_size,
      acceptedFiles: schema.file_restrictions?.accepted_mime_types.join(','),
      convertToBase64: true,
    },
    array: {
      type: 'custom',
      name: key,
      Field: ({ ...rest }) => {
        const formik = rest.formik;
        return <ArrayField item={{ name: key, schema }} formik={formik} />;
      },
      optionalValidation: true,
      validation: getYupForField(schema),
    },
  };

  if (schema.type === 'array') {
    return fieldTypeConfigs.array;
  }
  return {
    ...baseConfig,
    ...(fieldTypeConfigs[schema.type] || {}),
  };
};

export const convertSchemaToFormConfig = (schema) => {
  if (!schema) return [];

  const formConfig = [];
  const sections = {};

  // Group schema fields into sections
  Object.entries(schema).forEach(([key, fieldSchema]) => {
    if (fieldSchema.type === 'object') {
      sections[key] = {
        title: fieldSchema.label || key,
        fields: [],
        schema: fieldSchema,
      };
    } else {
      if (!sections['general']) {
        sections['general'] = {
          title: 'General Information',
          fields: [],
          schema: {},
        };
      }
      sections['general'].fields.push(
        convertFieldToFormConfig(key, fieldSchema)
      );
    }
  });

  // Create form configuration for each section
  Object.entries(sections).forEach(([sectionKey, sectionData]) => {
    // Skip only non-object sections that have no fields
    if (
      sectionData.fields.length === 0 &&
      sectionData.schema.type !== 'object'
    ) {
      return;
    }

    const sectionConfig = createSectionConfig(sectionKey, sectionData.title, [
      ...sectionData.fields,
    ]);

    // Process object type sections with nested properties
    if (sectionData.schema.type === 'object' && sectionData.schema.properties) {
      const processed = processObjectProperties(
        sectionData.schema.properties,
        sectionKey
      );

      sectionConfig.fields.push(
        ...processed.fields,
        ...processed.nestedSections
      );
    }

    formConfig.push(sectionConfig);
  });

  return formConfig;
};

// Helper function to create validation with required logic
const createValidationWithRequired = (baseValidation, propSchema, messages) => {
  return baseValidation.typeError(messages.typeError).when([], {
    is: () => propSchema.required,
    then: (schema) => schema.required(messages.required),
  });
};

const getTypeErrorMessage = (propSchema) => {
  const typeMessages = {
    number: 'must be a number',
    text: 'must be text',
    date: 'must be a valid date',
    array: 'must be a list',
    boolean: 'must be true or false',
    single_select: 'must be a valid selection',
    multi_select: 'must be valid selections',
    file: 'must be a valid file',
    checkbox: 'must be true or false',
  };

  return `${capitalizeWords(propSchema.label)} ${
    typeMessages[propSchema.type] || 'must be valid'
  }`;
};

export const getValidationMessages = (propSchema) => {
  const capitalizedLabel = capitalizeWords(propSchema.label);

  const getArrayRangeMessage = () => {
    const itemType =
      propSchema.type === 'multi_select' ? 'selections' : 'items';
    const minItems = propSchema.min_items || 0;
    const maxItems = propSchema.max_items;

    if (minItems && maxItems) {
      return `You must select between ${minItems} and ${maxItems} ${itemType}.`;
    } else if (minItems) {
      return `You must select at least ${minItems} ${itemType}.`;
    } else if (maxItems) {
      return `You must select at most ${maxItems} ${itemType}.`;
    }
    return `You must add between ${minItems} and ${maxItems} ${itemType}.`;
  };

  return {
    required: `${capitalizedLabel} is required`,
    typeError: getTypeErrorMessage(propSchema),
    numberRange:
      propSchema.min != null && propSchema.max != null
        ? `${capitalizedLabel} must be between ${propSchema.min} and ${propSchema.max}`
        : propSchema.min != null
        ? `${capitalizedLabel} must be at least ${propSchema.min}`
        : `${capitalizedLabel} must be at most ${propSchema.max}`,
    stringRange:
      propSchema.min_length != null && propSchema.max_length != null
        ? `${capitalizedLabel} must be between ${propSchema.min_length} and ${propSchema.max_length} characters`
        : propSchema.min_length != null
        ? `${capitalizedLabel} must be at least ${propSchema.min_length} characters`
        : `${capitalizedLabel} must be at most ${propSchema.max_length} characters`,
    arrayRange: getArrayRangeMessage(),
  };
};

export const getYupForField = (propSchema) => {
  const messages = getValidationMessages(propSchema);

  switch (propSchema.type) {
    case 'number':
      return createValidationWithRequired(Yup.number(), propSchema, messages)
        .min(propSchema.min ?? -Infinity, messages.numberRange)
        .max(propSchema.max ?? Infinity, messages.numberRange);

    case 'text':
      return createValidationWithRequired(Yup.string(), propSchema, messages)
        .min(propSchema.min_length ?? 0, messages.stringRange)
        .max(propSchema.max_length ?? Infinity, messages.stringRange);

    case 'date':
      return createValidationWithRequired(Yup.date(), propSchema, messages);

    case 'checkbox':
      return createValidationWithRequired(Yup.boolean(), propSchema, messages);

    case 'single_select':
    case 'file':
      return createValidationWithRequired(Yup.mixed(), propSchema, messages);

    case 'multi_select': {
      let multiSelectSchema = Yup.array().typeError(messages.typeError);

      if (propSchema.min_items || propSchema.max_items) {
        multiSelectSchema = multiSelectSchema.test(
          'item-count-range',
          messages.arrayRange,
          function (value) {
            if (!value) return true;
            const count = value.length;
            if (propSchema.min_items && count < propSchema.min_items)
              return false;
            if (propSchema.max_items && count > propSchema.max_items)
              return false;
            return true;
          }
        );
      }

      return createValidationWithRequired(
        multiSelectSchema,
        propSchema,
        messages
      );
    }

    case 'array': {
      let arrSchema = Yup.array().typeError(messages.typeError);

      if (propSchema.min_items || propSchema.max_items) {
        arrSchema = arrSchema.test(
          'item-count-range',
          messages.arrayRange,
          function (value) {
            if (!value) return true;
            const count = value.length;
            if (propSchema.min_items && count < propSchema.min_items)
              return false;
            if (propSchema.max_items && count > propSchema.max_items)
              return false;
            return true;
          }
        );
      }

      if (propSchema.item_type && propSchema.item_type.properties) {
        arrSchema = arrSchema.of(
          Yup.object(
            Object.fromEntries(
              Object.entries(propSchema.item_type.properties).map(
                ([key, childSchema]) => [key, getYupForField(childSchema)]
              )
            )
          )
        );
      }

      return arrSchema;
    }

    default:
      return propSchema.required
        ? Yup.mixed().required(messages.required).typeError(messages.typeError)
        : Yup.mixed().typeError(messages.typeError);
  }
};
